import { DateFormatter, parseDate, today } from "@internationalized/date";
import { useQuery } from "@tanstack/react-query";
import clsx from "clsx";
import { Check, ChevronDown, ChevronLeft, ChevronRight } from "lucide-react";
import { useState } from "react";
import {
  Button as AriaButton,
  Button,
  composeRenderProps,
  DateField,
  DateInput,
  DateSegment,
  type DateValue,
  ListBox,
  ListBoxItem,
  MenuTrigger,
  Popover,
} from "react-aria-components";
import { twMerge } from "tailwind-merge";
import { tv } from "tailwind-variants";

import { ComboBox } from "@/components/base/combo-box";
import { focusRing } from "@/components/base/utils";
import { divisionsQueryOptions } from "@/data/divisions";
import type { Gender } from "@/db/schema/shared";
import { useTRPC } from "@/integrations/trpc/react";
import { getDefaultTimeZone } from "@/lib/dates";
import { getGenderDisplay } from "@/utils/tournament";

import { itemStyles, popoverStyles } from "./multi-select";
import {
  Description,
  Errors,
  type FieldProps,
  inputStyles,
  Label,
} from "./shared";

const dateFormatter = new DateFormatter("en-US", {
  month: "short",
  day: "numeric",
  year: "numeric",
});

export type DivisionPickerFieldProps = FieldProps & {
  className?: string;
  isDisabled?: boolean;
  selectedDivisionIds: number[];
  dateRange?: { startDate?: string; endDate?: string };
  divisionIds?: number[];
  venueIds?: number[];
  minDivisionOrder?: number;
  isJunior?: boolean;
  teamSize?: number;
  // Divisions are never "unknown" — that value exists only for anonymized profiles.
  gender?: Exclude<Gender, "unknown">;
};

export const segmentStyles = tv({
  base: "inline p-0.5 type-literal:px-0 rounded-xs outline outline-0 forced-color-adjust-none caret-transparent text-gray-800 forced-colors:text-[ButtonText]",
  variants: {
    isPlaceholder: {
      true: "text-placeholder italic",
    },
    isDisabled: {
      true: "cursor-not-allowed text-gray-400 forced-colors:text-[GrayText]",
    },
    isFocused: {
      true: "bg-blue-200 text-black forced-colors:bg-[Highlight] forced-colors:text-[HighlightText]",
    },
  },
});

export function formatTournamentDivision(td: {
  gender: Gender;
  name: string | null;
  division: { display: string | null; name: string; maxAge?: number | null };
  tournament: {
    date: string;
    venue: { name: string; city: string };
  };
}) {
  const divisionDisplay = td.division.display ?? td.division.name.toUpperCase();
  const genderDisplay = getGenderDisplay(td.gender, td.division.maxAge ?? null);
  const name = td.name ? `${td.name} ` : "";
  const date = dateFormatter.format(
    parseDate(td.tournament.date).toDate(getDefaultTimeZone()),
  );

  return `${genderDisplay} ${name}${divisionDisplay} - ${td.tournament.venue.name}, ${td.tournament.venue.city} - ${date}`;
}

export type FilterDropdownProps<T extends number> = {
  className?: string;
  label: string;
  options: { value: T; display: string }[];
  selectedValues: Set<T>;
  onSelectionChange: (values: Set<T>) => void;
};

export function FilterDropdown<T extends number>({
  className,
  label,
  options,
  selectedValues,
  onSelectionChange,
}: FilterDropdownProps<T>) {
  return (
    <MenuTrigger>
      <Button
        className={(render) =>
          clsx(
            "flex items-center justify-between text-start gap-2 cursor-default px-2 py-1 hover:bg-gray-200 text-sm min-w-24",
            focusRing(render),
            inputStyles(render),
            className,
          )
        }
      >
        <span
          className={selectedValues.size === 0 ? "text-placeholder italic" : ""}
        >
          {selectedValues.size === 0
            ? label
            : `${label} (${selectedValues.size})`}
        </span>
        <ChevronDown
          aria-hidden
          className="h-4 w-4 text-gray-600 group-disabled:text-gray-200 forced-colors:text-[ButtonText] forced-colors:group-disabled:text-[GrayText]"
        />
      </Button>

      <Popover
        className={composeRenderProps(
          "min-w-(--trigger-width)",
          (className, render) => popoverStyles({ ...render, className }),
        )}
      >
        <ListBox
          aria-label={label}
          items={options}
          selectionMode="multiple"
          selectionBehavior="toggle"
          selectedKeys={selectedValues}
          onSelectionChange={(keys) => {
            if (keys === "all") {
              onSelectionChange(new Set(options.map((o) => o.value)));
            } else {
              onSelectionChange(keys as Set<T>);
            }
          }}
          className="bg-popover max-h-60 overflow-auto rounded-lg border border-gray-300 p-1 shadow-lg outline-hidden outline-0 [clip-path:inset(0_0_0_0_round_.75rem)]"
        >
          {options.map(({ value, display }) => (
            <ListBoxItem key={value} id={value} className={itemStyles}>
              {({ isSelected }) => (
                <>
                  <span className="group-selected:font-semibold flex flex-1 items-center gap-2 truncate font-normal">
                    {display}
                  </span>
                  <span className="flex w-5 items-center">
                    {isSelected && <Check className="h-4 w-4" />}
                  </span>
                </>
              )}
            </ListBoxItem>
          ))}
        </ListBox>
      </Popover>
    </MenuTrigger>
  );
}

const PAGE_SIZE = 25;

export function DivisionPickerField({
  className,
  label,
  description,
  field,
  isDisabled,
  selectedDivisionIds,
  dateRange,
  divisionIds: initialDivisionIds,
  venueIds: initialVenueIds,
  minDivisionOrder,
  isJunior,
  teamSize,
  gender,
}: DivisionPickerFieldProps) {
  const [filterDivisionIds, setFilterDivisionIds] = useState<Set<number>>(
    () => new Set(initialDivisionIds ?? []),
  );
  const [filterVenueIds, setFilterVenueIds] = useState<Set<number>>(
    () => new Set(initialVenueIds ?? []),
  );
  const [filterStartDate, setFilterStartDate] = useState<DateValue | null>(
    dateRange?.startDate
      ? parseDate(dateRange.startDate)
      : today(getDefaultTimeZone()),
  );
  const [filterEndDate, setFilterEndDate] = useState<DateValue | null>(null);
  const [page, setPage] = useState(1);

  const { data: divisions = [] } = useQuery({
    ...divisionsQueryOptions(),
    select: (divisions) =>
      divisions
        .filter((d) =>
          isJunior != null
            ? isJunior
              ? d.maxAge != null
              : d.maxAge == null
            : true,
        )
        .map(({ id, display, name }) => ({
          value: id,
          display: display ?? name.toUpperCase(),
        })),
  });

  const trpc = useTRPC();
  const { data: venues = [] } = useQuery({
    ...trpc.venues.list.queryOptions(),
    select: (venues) =>
      venues.map(({ id, name, city }) => ({
        value: id,
        display: `${name}, ${city}`,
      })),
  });

  const searchParams = {
    divisionIds: Array.from(filterDivisionIds),
    venueIds: Array.from(filterVenueIds),
    startDate: filterStartDate?.toString() ?? dateRange?.startDate,
    endDate: filterEndDate?.toString() ?? dateRange?.endDate,
    excludeIds: selectedDivisionIds,
    minDivisionOrder,
    isJunior,
    teamSize,
    gender,
    paging: { page, size: PAGE_SIZE },
  };

  const { data, isLoading } = useQuery(
    trpc.tournaments.searchDivisions.queryOptions(searchParams),
  );

  const tournamentDivisions = data?.data ?? [];
  const totalPages = data?.pageInfo.totalPages ?? 1;
  const totalCount = data?.pageInfo.totalItems ?? 0;

  // Reset to page 1 when filters change
  const handleFilterChange =
    <T,>(setter: (value: T) => void) =>
    (value: T) => {
      setter(value);
      setPage(1);
    };

  return (
    <div className={twMerge("flex flex-col gap-2", className)}>
      {label && <Label>{label}</Label>}

      <div className="flex flex-wrap items-center gap-2">
        <DateField
          aria-label="Start date"
          value={filterStartDate}
          onChange={handleFilterChange(setFilterStartDate)}
          isDisabled={isDisabled}
          className="flex-shrink-0"
        >
          <DateInput
            className={(renderProps) =>
              inputStyles({ ...renderProps, class: "px-2 py-1 text-sm" })
            }
          >
            {(segment) => (
              <DateSegment segment={segment} className={segmentStyles} />
            )}
          </DateInput>
        </DateField>

        <span className="text-gray-500">to</span>

        <DateField
          aria-label="End date"
          value={filterEndDate}
          onChange={handleFilterChange(setFilterEndDate)}
          isDisabled={isDisabled}
          className="flex-shrink-0"
        >
          <DateInput
            className={(renderProps) =>
              inputStyles({ ...renderProps, class: "px-2 py-1 text-sm" })
            }
          >
            {(segment) => (
              <DateSegment segment={segment} className={segmentStyles} />
            )}
          </DateInput>
        </DateField>

        <FilterDropdown
          label="Divisions"
          options={divisions}
          selectedValues={filterDivisionIds}
          onSelectionChange={handleFilterChange(setFilterDivisionIds)}
        />
      </div>

      <ComboBox
        aria-label="Locations"
        placeholder="All locations"
        items={venues}
        selectedKeys={filterVenueIds}
        multi
        onSelectionChange={(key) => {
          if (key != null) {
            handleFilterChange(setFilterVenueIds)(
              new Set([...filterVenueIds, key as number]),
            );
          }
        }}
        onRemove={(key) => {
          const next = new Set(filterVenueIds);
          next.delete(key as number);
          handleFilterChange(setFilterVenueIds)(next);
        }}
      />

      <ListBox
        aria-label="Tournament divisions"
        selectionMode="single"
        disallowEmptySelection={false}
        selectedKeys={
          field.state.value != null ? new Set([field.state.value]) : new Set()
        }
        onSelectionChange={(keys) => {
          if (keys !== "all" && keys.size > 0) {
            const selectedId = Array.from(keys)[0] as number;
            field.handleChange(selectedId);
          }
        }}
        className={clsx(
          "border border-gray-300 rounded-lg p-1 max-h-64 overflow-auto bg-white",
          (isDisabled || isLoading) && "opacity-50 pointer-events-none",
        )}
        renderEmptyState={() => (
          <div className="p-4 text-center text-gray-500 italic">
            {isLoading ? "Loading..." : "No tournament divisions found"}
          </div>
        )}
      >
        {tournamentDivisions.map((td) => {
          const displayText = formatTournamentDivision(td);
          return (
            <ListBoxItem
              key={td.id}
              id={td.id}
              className={itemStyles}
              textValue={displayText}
            >
              {({ isSelected }) => (
                <>
                  <span className="group-selected:font-semibold flex flex-1 items-center gap-2 font-normal whitespace-nowrap">
                    {displayText}
                  </span>
                  <span className="flex w-5 items-center">
                    {isSelected && <Check className="h-4 w-4" />}
                  </span>
                </>
              )}
            </ListBoxItem>
          );
        })}
      </ListBox>

      {field.state.value != null &&
        (() => {
          const selected = tournamentDivisions.find(
            (td) => td.id === field.state.value,
          );
          return selected ? (
            <p className="text-sm break-words text-gray-700">
              <span className="font-medium">Selected:</span>{" "}
              {formatTournamentDivision(selected)}
            </p>
          ) : null;
        })()}

      {totalPages > 1 && (
        <div className="flex items-center justify-between text-sm text-gray-600">
          <span>
            {totalCount} tournament{totalCount !== 1 ? "s" : ""}
          </span>
          <div className="flex items-center gap-2">
            <AriaButton
              isDisabled={page <= 1 || isLoading}
              onPress={() => setPage((p) => Math.max(1, p - 1))}
              className="rounded p-1 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <ChevronLeft className="h-4 w-4" />
            </AriaButton>
            <span>
              Page {page} of {totalPages}
            </span>
            <AriaButton
              isDisabled={page >= totalPages || isLoading}
              onPress={() => setPage((p) => Math.min(totalPages, p + 1))}
              className="rounded p-1 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <ChevronRight className="h-4 w-4" />
            </AriaButton>
          </div>
        </div>
      )}

      {description && <Description>{description}</Description>}
      <Errors field={field} />
    </div>
  );
}
